/**
 * sessionLifecycle.js
 *
 * Jedno mesto za "kraj sesije" na frontu.
 * Poziva se iz: axios interceptor (401/403), socket disconnect, ručnog logout-a.
 *
 * Cilj: ne ostaviti korisnika na /user ili /admin sa zastarelim podacima u useAppStore
 * kada server više ne priznaje sesiju ili kada socket više ne sync-uje promene.
 */

import axios from 'axios';

import useAuthStore from '../store/useAuthStore';
import useAppStore from '../store/useAppStore';

const API_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Sprečava da više paralelnih API grešaka (npr. 5× 401 odjednom)
 * pokrene forceSessionEnd pet puta zaredom.
 */
let isEndingSession = false;

// ---------------------------------------------------------------------------
// AUTH_PUBLIC_PATHS + isPublicAuthRequest
// ---------------------------------------------------------------------------
//
// ŠTA JE OVO?
// Lista API ruta gde 401/403 NE znači "izloguj me iz aplikacije", već
// "pogrešan login", "pogrešan kod", "istekao kod za reset", itd.
//
// ZAŠTO POSTOJI?
// Axios interceptor hvata SVE axios greške globalno. Bez izuzetka,
// pogrešna lozinka na /api/auth/login bi takođe pozvala forceSessionEnd
// i očistila store — što nema smisla jer korisnik još nije ulogovan.
//
// KAKO RADI isPublicAuthRequest(url)?
// Proverava da li URL zahteva sadrži neki od tih path-ova (includes).
// Primer: "http://localhost:3000/api/auth/login" → true → preskoči logout.
//
const AUTH_PUBLIC_PATHS = [
    '/api/auth/login',
    '/api/auth/forgot-password',
    '/api/auth/forgot-password-submit-code',
    '/api/auth/reset-password',
];

const isPublicAuthRequest = (url = '') =>
    AUTH_PUBLIC_PATHS.some((path) => url.includes(path));

// ---------------------------------------------------------------------------
// isSessionInvalidResponse
// ---------------------------------------------------------------------------
//
// ŠTA JE OVO?
// Odlučuje da li axios greška znači "sesija više ne važi → forceSessionEnd".
//
// KAKO RADI?
// 1) status 401 — server authenticate middleware:
//    "Authentication required" / "Not authenticated" kada nema req.session.userId
//
// 2) status 403 + poruka sadrži "logged out" — security slučajevi u server.js
//    gde server uništi sesiju (session.destroy) i vrati poruku tipa
//    "You have been logged out for security reasons."
//    Običan 403 (npr. zabranjen pristup) NE bi trebalo da izloguje korisnika,
//    zato gledamo tekst poruke, ne samo status kod.
//
const isSessionInvalidResponse = (error) => {
    const status = error?.response?.status;
    const message = String(
        error?.response?.data?.error || error?.response?.data?.message || ''
    ).toLowerCase();

    if (status === 401) {
        return true;
    }

    if (status === 403 && message.includes('logged out')) {
        return true;
    }

    return false;
};

/**
 * Dinamički import socket-a da izbegnemo circular dependency:
 * employeesSocket.js će kasnije importovati forceSessionEnd iz ovog fajla.
 */
const disconnectAppSocket = async () => {
    const { socket } = await import('../socket/employeesSocket');
    if (socket.connected) {
        socket.disconnect();
    }
};

/**
 * Jedinstveni kraj sesije na frontu.
 *
 * @param {Object} [options]
 * @param {string} [options.reason] - za debug log (npr. 'socket_disconnect', 'api_401')
 * @param {boolean} [options.callServerLogout] - true samo za ručni logout (ProfileModal)
 */
export const forceSessionEnd = async ({
    reason = 'unknown',
    callServerLogout = false,
} = {}) => {
    if (isEndingSession) {
        return;
    }

    isEndingSession = true;

    try {
        console.warn('[session] ending session:', reason);

        // 1) Prekini socket — bez live sync-a ne verujemo podacima u memoriji
        await disconnectAppSocket();

        // 2) Reset useAppStore (resetAppState dodaje se u useAppStore.js)
        const resetAppState = useAppStore.getState().resetAppState;
        if (typeof resetAppState === 'function') {
            resetAppState();
        }

        // 3) Očisti auth — App.jsx ProtectedRoute vodi na /login kad userRole === null
        useAuthStore.setState({
            userRole: null,
            userId: null,
            isHR: false,
            loading: false,
            error: null,
        });

        // 4) Samo ručni logout: obavesti server da uništi sesiju i diskonektuje socket-e
        if (callServerLogout) {
            try {
                await axios.get(`${API_URL}/api/auth/logout`);
            } catch {
                // Sesija je možda već istekla — nije kritično
            }
        }
    } finally {
        setTimeout(() => {
            isEndingSession = false;
        }, 500);
    }
};

/**
 * Registruje globalni axios response interceptor (pozvati jednom u index.jsx).
 *
 * Na svaki neuspešan axios odgovor proverava da li treba forceSessionEnd.
 * Uspešni odgovori prolaze neizmenjeni.
 */
export const setupAxiosSessionInterceptor = () => {
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const config = error?.config || {};
            const url = config.url || '';

            // Ručno isključenje po pozivu: axios.get(url, { skipSessionHandler: true })
            if (config.skipSessionHandler) {
                return Promise.reject(error);
            }

            // Login / forgot-password — 401 ovde je "pogrešan kod", ne istekla sesija
            if (isPublicAuthRequest(url)) {
                return Promise.reject(error);
            }

            // checkSession na /api/auth/me već postavlja userRole: null na 401
            if (url.includes('/api/auth/me')) {
                return Promise.reject(error);
            }

            if (isSessionInvalidResponse(error)) {
                forceSessionEnd({ reason: `api_${error.response?.status}` });
            }

            return Promise.reject(error);
        }
    );
};
